<template>
    <section aria-live="polite" v-if="chatStarted">
        <div v-for="message in messages" :id="`message-${message.sender}-${message.msg_index}`" :key="message.text">
          <RichComponent v-if="message.sender === 'client'" me>
            <RichBubble v-if="message.text" :text="message.text" me :link="formattedText(message.text)" :senderName="this.chatInitialMessage" :time="message.time" />
            <div v-if="message.type === 'FILE'">
              <div v-for="file in message.fileList" :key="file.name">
                <FilePreview :file="file" :download="true" />
              </div>
            </div>
          </RichComponent>
          <RichComponent v-if="message.sender === 'agent'" fullwidth>
            <RichBubble v-if="message.type === messageTypes.TEXT" :text="message.text" :link="formattedText(message.text)" :senderName="message.senderName" :time="message.time" />

            <RichLink v-if="message.type === messageTypes.LINK" :url="message.url" :urlHandling="message.urlHandling" :urlText="message.urlText" :urlPreview="message.urlPreview" />

            <RichPicture v-if="message.type === messageTypes.IMAGE" :uri="message.url" :title="message.title" />

            <RichCard
                v-if="message.type === messageTypes.CARD"
                :title="message.title"
                :subtitle="message.subtitle"
                :image-uri="message.imageUri"
                :image-title="message.imageTitle"
                height="100%"
                :text="message.text">
              <RichCardButton
                  v-for="button in message.cardButtons"
                  :key="button.title"
                  :uri="button.url"
                  :title="button.title"
                  :urlHandling="button.urlHandling"
              />
            </RichCard>

            <RichCarousel
                v-if="message.type === messageTypes.CAROUSEL_SELECT"
                :items="getCarouselSelectItems(message)">
            </RichCarousel>

            <RichList v-if="message.type === messageTypes.CAROUSEL_BROWSE">
              <RichListItem
                  v-for="item in getCarouselBrowseItems(message)"
                  :key="item.key"
                  :uri="item.openUrlAction.url"
                  :title="item.title"
                  :description="item.description"
                  :footer="item.footer"
                  :image-uri="item.imageUrl"
                  :image-title="item.imageTitle"
                  urlHandling="_blank"
              />
            </RichList>

            <RichComponent v-if="message.type === messageTypes.AUDIO" fullwidth>
              <RichAudio v-for="item in getAudioItems(message)"
                         :key="item.name"
                         :url="item.contentUrl"
                         :icon="item.icon"
                         :title="item.name"
                         :description="item.description"
              ></RichAudio>
            </RichComponent>

            <RichList v-if="message.type === messageTypes.LIST" fullwidth>
              <RichListItem
                  v-for="item in getListItems(message)"
                  :key="item.key"
                  :title="item.title"
                  :description="item.subtitle"
                  :footer="item.footer"
                  :image-uri="item.imageUrl"
                  :image-title="item.imageTitle"
                  urlHandling="_self">
                <RichTypeList :items="item.items" />
              </RichListItem>
            </RichList>

            <div v-if="message.type === messageTypes.TABLE">
              <RichTableCard
                  :rows="getTableItems(message).rows"
                  :header="getTableItems(message).columnProperties">
              </RichTableCard>
            </div>

            <div v-if="message.type === messageTypes.MID_CHAT_FORM">
              <MidChatForm :inputs="message.dataForm" :userDetails="userDetails"  @submit="submitMidChatForm" :id="`mid-form-${message.msg_index}`" />
            </div>

            <RichComponent v-if="message.type === messageTypes.FILE" fullwidth>
              <div v-for="file in getFileItems(message)" :key="file.name">
                <FilePreview
                    :file="file"
                    :inverse="true"
                />
              </div>
            </RichComponent>
          </RichComponent>

          <RichComponent :fullwidth="message.type === messageTypes.SYSTEM" v-if="message.type === messageTypes.SYSTEM">
            <RichSystem :text="message.text"></RichSystem>
          </RichComponent>
        </div>
        <LoaderComponent v-if="loading" />
      </section>
</template>

<script>
import RichComponent from '@/components/RichComponent.vue'
import RichBubble from '@/components/RichBubble.vue'
import FilePreview from '@/components/common/FilePreview.vue'
import RichLink from '@/components/RichLink.vue'
import RichPicture from '@/components/RichPicture.vue'
import RichCard from '@/components/RichCard.vue'
import RichCardButton from '@/components/RichCardButton.vue'
import RichCarousel from '@/components/RichCarousel.vue'
import RichList from '@/components/RichList.vue'
import RichListItem from '@/components/RichListItem.vue'
import RichAudio from '@/components/RichAudio.vue'
import RichTableCard from '@/components/RichTableCard.vue'
import RichTypeList from '@/components/RichTypeList.vue'
import LoaderComponent from '@/components/common/LoaderComponent.vue'
import MidChatForm from '@/components/forms/MidChatForm.vue'
import RichSystem from '@/components/RichSystem.vue'
import { messageTypes } from '@/utils/constants'
import { useWidgetStore } from '@/store'
import { mapState } from 'pinia'

export default {
  name: 'ChatContainer',
  components: {
    RichComponent,
    RichBubble,
    RichLink,
    RichPicture,
    RichCard,
    RichCardButton,
    RichCarousel,
    RichList,
    RichListItem,
    RichTypeList,
    RichTableCard,
    RichAudio,
    RichSystem,
    FilePreview,
    LoaderComponent,
    MidChatForm
  },
  props: {
    messages: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    showAdditionalModal: {
      type: Boolean,
      default: false
    },
    userDetails: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      messageTypes
    }
  },
  computed: {
    ...mapState(useWidgetStore, [
      'chatStarted',
      'chatInitialMessage'
    ])
  },
  emits: ['submitMidChatForm'],
  methods: {
    formattedText (text) {
      let anchorTag = ''
      if (!text) {
        return false
      }
      if (text.match(/[\]^[\w.]+@([\w-]+\.)+[\w-]{2,4}/)) {
        anchorTag = '<a href="mailto:$1" class="anchorTag-chat-msg">$1</a>'
      } else if (text.match(/(https?:\/\/[^\s]+)/g)) {
        anchorTag = '<a href="$1" target="_blank" class="anchorTag-chat-msg">$1</a>'
      } else {
        anchorTag = '<a href="//$1" target="_blank" class="anchorTag-chat-msg">$1</a>'
      }
      // eslint-disable-next-line prefer-regex-literals
      const urlRegex = /((https?:\/\/[^\s]+)|(www[^\s]+)|[\]^[\w.]+@([\w-]+\.)+[\w-]{2,4})/g
      return urlRegex.test(text) ? text.replace(urlRegex, anchorTag) : false
    },
    submitMidChatForm (formData) {
      this.$emit('submitMidChatForm', formData)
    },
    getCarouselBrowseItems (data) {
      return data.payload.google.richResponse.items[0].carouselBrowse.items || []
    },
    getCarouselSelectItems (data) {
      return data.payload.google.richResponse.items[0].carouselSelect.items || []
    },
    getTableItems (data) {
      return data.payload.google.richResponse.items[0].tableCard || []
    },
    getFileItems (data) {
      return data.fileList
    },
    getAudioItems (data) {
      return data.payload.google.richResponse.items[0].mediaResponse.mediaObjects || []
    },
    getListItems (data) {
      if (data) {
        return data.richContent[0] || []
      }

      return []
    }
  }
}
</script>
